import { useEffect, useRef } from "react"
import { useRouter } from "next/router"
import { getWindow } from "utils-lib/getWindow"
import getIsRouteNextJsEnabled from "utils-lib/nextJs/getIsRouteNextJsEnabled"
import isLocalRouteValidationEnabled from "utils-lib/nextJs/isLocalRouteValidationEnabled"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"

const window = getWindow()

const hashRegex = /.+#/

// This is improved route listening logic to use a combination of router events and popstate events to detect internal and external navigation.
// An internal popstate event is triggered after the corresponding router "routeChangeStart" event and before the "routeChangeComplete" event.
// If a popstate event is triggered without a corresponding router event, it is considered an external navigation.
// This handles the scenario where the user triggers a browser forward/back navigation after arriving in the next js app from the legacy aem-spa app.
// This causes a mismatch between the browser URL and the page content and we need to trigger a router.reload() to send the user back to our legacy aem-spa app.
// This is an edge case caused by the transition between the legacy aem-spa app and the next js app, it is not reproducible locally.
// This is not a perfect solution, if you brute force smash the app with a high amount of crazy navigations like a normal user would never do, you might encounter a false positive.
// That is very rare, and much less likely to happen than with the previous implementation which would reload the page on popstate events more frequently, and after migrating to next 15 would always reload the page on popstate events.
// I played around with some more optimizations but this gives us the most stable behavior.
// Once we are fully migrated to next js, we can remove this logic (with the exception of select mywm routes, it'll be much simpler)
const RouteChangeListener = () => {
  const router = useRouter()
  // Use a ref to track internal vs external navigation, default to false to catch the scenario where a router event isn't triggered.
  const isInternalNavigation = useRef(false)
  const { LDFFRouteEnhancedNextJsRouting } = useFeatureFlags([
    "LDFFRouteEnhancedNextJsRouting",
  ])

  useEffect(() => {
    // Listen for external route changes using popstate events
    const handlePopState = () => {
      const routerPathname = router.asPath

      // If the popstate event is triggered without a corresponding router event, isInternalNavigation will be false and it is considered an external navigation.
      // We need to trigger a hard reload, otherwise we'll be stuck in a mismatch between the browser URL and the page content.
      if (
        !isInternalNavigation.current &&
        // router pathname can contain a hash whereas window.location.pathname which never contains a hash
        !hashRegex.test(router.asPath) &&
        // if the router pathname doesn't contain a hash, and the window location pathname doesn't match the router pathname, trigger a router.reload()
        window.location.pathname !== routerPathname
      ) {
        router.reload()
      } else {
        // If the popstate event is triggered after a router event, it is considered an internal navigation and we need to reset the isInternalNavigation flag to the default value.
        isInternalNavigation.current = false
      }
    }

    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleRouteChangeStart = async (pathname) => {
      if (!hashRegex.test(pathname)) {
        // Skip validating hash urls if we didn't come from the legacy aem-spa app, next/router doesn't separate hashes from the pathname like window.location does.
        // This handler is called before the popstate event, so we can update the isInternalNavigation flag here.
        // If the popstate event is triggered, it will be considered an internal navigation.
        isInternalNavigation.current = true
      }
    }
    const handleRouteChangeEnd = (pathname) => {
      const isLocal = process.env.NEXT_PUBLIC_IS_LOCAL_ENV

      const isNextJsEnabled = getIsRouteNextJsEnabled(
        pathname,
        LDFFRouteEnhancedNextJsRouting,
      )

      if (isLocal && !isLocalRouteValidationEnabled()) {
        return
      }

      // all conditional routing on click has been moved to the Link component, this is still needed for handling browser back/forward navigation properly
      if (!isNextJsEnabled) {
        // reload the page to route traffic to the legacy aem-spa app
        router.reload()
      }
    }

    router.events.on("routeChangeStart", handleRouteChangeStart)
    router.events.on("routeChangeComplete", handleRouteChangeEnd)

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart)
      router.events.off("routeChangeComplete", handleRouteChangeEnd)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router])

  return null
}

export default RouteChangeListener
